import { ReactElement, SVGProps, useId } from 'react';

type IconProps = SVGProps<SVGSVGElement>;
export type IconComponent = (props: IconProps) => ReactElement;

// Çizgi ikonlar - boyut ve renk üst elemandan (width/height + currentColor) gelir
const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const filled = {
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
};

// Hizmet ikonları
export const TowTruckIcon = (props: IconProps) => (
  <svg {...stroke} viewBox="0 0 48 48" strokeWidth={2.5} {...props}>
    <rect x="4" y="22" width="28" height="14" rx="3" />
    <path d="M32 28 L40 28 L44 34 L44 36 L32 36 Z" />
    <circle cx="12" cy="38" r="4" />
    <circle cx="38" cy="38" r="4" />
    <path d="M4 22 L8 12 L22 12 L26 22" />
    <line x1="16" y1="22" x2="16" y2="12" />
  </svg>
);

export const CraneIcon = (props: IconProps) => (
  <svg {...stroke} viewBox="0 0 48 48" strokeWidth={2.5} {...props}>
    <line x1="12" y1="44" x2="12" y2="8" />
    <line x1="12" y1="8" x2="38" y2="8" />
    <line x1="38" y1="8" x2="38" y2="20" />
    <line x1="38" y1="20" x2="32" y2="20" />
    <rect x="30" y="20" width="8" height="8" rx="1" />
    <line x1="6" y1="44" x2="18" y2="44" />
    <line x1="12" y1="8" x2="6" y2="20" />
    <line x1="12" y1="8" x2="18" y2="20" />
  </svg>
);

// Wrench ve direksiyon 24'lük kutuda çizili; 48'lik hizmet ikonlarıyla aynı çizgi
// kalınlığında görünmeleri için strokeWidth düşürüldü
export const WrenchIcon = (props: IconProps) => (
  <svg {...stroke} strokeWidth={1.25} {...props}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

export const MovingTruckIcon = (props: IconProps) => (
  <svg {...stroke} viewBox="0 0 48 48" strokeWidth={2.5} {...props}>
    <rect x="4" y="14" width="26" height="22" rx="2" />
    <path d="M30 24 L40 24 L44 32 L44 36 L30 36 Z" />
    <circle cx="14" cy="38" r="4" />
    <circle cx="38" cy="38" r="4" />
    <rect x="10" y="20" width="8" height="8" rx="1" />
    <line x1="22" y1="20" x2="22" y2="28" />
  </svg>
);

export const SteeringWheelIcon = (props: IconProps) => (
  <svg {...stroke} strokeWidth={1.25} {...props}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 14v8" />
    <path d="M10 12H2" />
    <path d="M14 12h8" />
  </svg>
);

export const ShuttleVanIcon = (props: IconProps) => (
  <svg {...stroke} viewBox="0 0 48 48" strokeWidth={2.5} {...props}>
    <rect x="4" y="14" width="40" height="20" rx="3" />
    <line x1="4" y1="24" x2="44" y2="24" />
    <line x1="16" y1="14" x2="16" y2="24" />
    <line x1="28" y1="14" x2="28" y2="24" />
    <circle cx="14" cy="38" r="3" />
    <circle cx="34" cy="38" r="3" />
  </svg>
);

export const CrownIcon = (props: IconProps) => (
  <svg {...stroke} viewBox="0 0 48 48" strokeWidth={2.5} {...props}>
    <path d="M6 16 L14 24 L24 8 L34 24 L42 16 L38 40 L10 40 Z" />
    <line x1="10" y1="40" x2="38" y2="40" />
  </svg>
);

export type ServiceKey =
  | 'cekici'
  | 'vinc'
  | 'nakliye'
  | 'yol-yardim'
  | 'servis'
  | 'vip-tasima'
  | 'surucu';

export const serviceIcons: Record<ServiceKey, IconComponent> = {
  cekici: TowTruckIcon,
  vinc: CraneIcon,
  nakliye: MovingTruckIcon,
  'yol-yardim': WrenchIcon,
  servis: ShuttleVanIcon,
  'vip-tasima': CrownIcon,
  surucu: SteeringWheelIcon,
};

// Genel arayüz ikonları
export const LightbulbIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

export const NewspaperIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
    <path d="M18 14h-8" />
    <path d="M15 18h-5" />
    <path d="M10 6h8v4h-8V6Z" />
  </svg>
);

export const FileTextIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

export const ClockIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const ZapIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const HeartIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

export const MapPinIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const ShieldCheckIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const SmileIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

export const SmartphoneIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

export const CreditCardIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

export const InfoIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export const CheckIcon = (props: IconProps) => (
  <svg {...stroke} strokeWidth={3} {...props}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const CheckCircleIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export const ChevronDownIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const CloseIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const ArrowRightIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// Marka ikonları (sosyal medya ve uygulama mağazaları)
export const FacebookIcon = (props: IconProps) => (
  <svg {...filled} {...props}>
    <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07" />
  </svg>
);

export const XIcon = (props: IconProps) => (
  <svg {...filled} {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const InstagramIcon = (props: IconProps) => (
  <svg {...stroke} {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

export const LinkedInIcon = (props: IconProps) => (
  <svg {...filled} {...props}>
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);

export const AppleIcon = (props: IconProps) => (
  <svg {...filled} {...props}>
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

// Renkli Google Play logosu; aynı sayfada birden fazla kullanılabilsin diye gradyan id'si benzersiz
export const GooglePlayColorIcon = (props: IconProps) => {
  const gradientId = `gplay-${useId().replace(/:/g, '')}`;
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00c3ff" />
          <stop offset="35%" stopColor="#00e676" />
          <stop offset="70%" stopColor="#ffcd00" />
          <stop offset="100%" stopColor="#ff3d3d" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l220.7-221.3 60.1 60.1L104.6 499z"
      />
    </svg>
  );
};

export const GooglePlayIcon = (props: IconProps) => (
  <svg {...filled} {...props}>
    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
  </svg>
);
